import { createSharedComposable, useLocalStorage } from "@vueuse/core";
import { computed, onMounted, shallowRef } from "vue";
import { setLocale as setLocaleForYup } from "yup";
import { IS_DEVELOPMENT } from "@/core/constants";
import { extractHostname } from "@/core/utilities";
import { useUser } from "@/shared/account/composables/useUser";
import { useFetch } from "../api/common/composables/useFetch";
import { getStore } from "../api/graphql";
import {
  countryCodeToLanguageAndCurrencyMap,
  languagesAvailableExclusivelyInCart,
  OVERLAY_LOCALES,
} from "../constants/language-and-currency";
import FeatureFlags from "../utilities/featureFlags";
import { useGeolocation } from "./useGeolocation";
import { useThemeContext } from "./useThemeContext";
import type { StoreResponseType } from "../api/graphql/types";
import type { ILanguage } from "../types";
import type { LanguageAndCurrency } from "../types/defaultLanguageCurrency";
import type { I18n } from "@/i18n";
import type { Composer } from "vue-i18n";

function _useLanguages() {
  const savedLocale = useLocalStorage<string>("locale", "");
  const savedLocaleCopy = useLocalStorage<string>("localeBeforeCartChange", "");
  const savedCartLanguage = useLocalStorage<string>("cartLocale", "");
  const userDeniedCartLanguageChange = useLocalStorage<boolean>("userDeniedCartLanguageChange", false);
  const { themeContext, fetchThemeContext } = useThemeContext();
  const { getCountryCode } = useGeolocation();
  const { user } = useUser();

  const defaultLanguage = computed<ILanguage>(() => themeContext.value.defaultLanguage);
  const defaultLocale = computed<string>(() => defaultLanguage.value.twoLetterLanguageName);

  const globallySupportedLanguages = computed<ILanguage[]>(() =>
    themeContext.value.availableLanguages.filter(
      (language) => languagesAvailableExclusivelyInCart.indexOf(language.twoLetterLanguageName) === -1,
    ),
  );

  const getIsCartPage = () => {
    const locationComponents = location.pathname.split("/");
    return (
      locationComponents.find((item) => item === "cart") !== undefined ||
      (locationComponents.find((item) => item === "checkout" || item === "quote") !== undefined &&
        !locationComponents.find((item) => item === "account"))
    );
  };
  const isCartPage = shallowRef<boolean>(getIsCartPage());

  const supportedLanguages = computed<ILanguage[]>(() => {
    const availableLanguages = isCartPage.value
      ? themeContext.value.availableLanguages
      : globallySupportedLanguages.value;

    const orderedLanguages = ["en", "zh", "ja", "de"];
    return availableLanguages.sort((a, b) => {
      const indexA = orderedLanguages.indexOf(a.twoLetterLanguageName);
      const indexB = orderedLanguages.indexOf(b.twoLetterLanguageName);

      return indexA - indexB;
    });
  });

  const supportedLocales = computed<string[]>(() => supportedLanguages.value.map((item) => item.twoLetterLanguageName));

  const contactLocale = computed(() => user.value?.contact?.defaultLanguage?.split("-")[0]);
  const countryCodeTLDLanguage = computed(() => {
    if (themeContext.value.storeSettings) {
      const featureFlags = new FeatureFlags(themeContext.value.storeSettings.modules);
      const ccTLDsValue = (featureFlags.getValue("Thorlabs.Commerce", "ccTLDs") as string) || "";
      const domains = ccTLDsValue ? (JSON.parse(ccTLDsValue) as Record<string, LanguageAndCurrency>) : {};

      const hostname = location.hostname;
      const strippedHostname = hostname.replace(/^www\./, "");

      return (domains[hostname] ?? domains[strippedHostname])?.Language;
    }

    return null;
  });

  const currentLocale = computed<string>(() => {
    const localeInPath = location.pathname.split("/")[1];
    const isLanguageSegment = themeContext.value.availableLanguages.some(
      (lang) => lang.twoLetterLanguageName === localeInPath,
    );
    if (localeInPath && !isLanguageSegment) {
      // Non-locale segment (e.g. a content slug). Signed-in users get their profile language;
      // then explicit language selection; then ccTLD mapping; otherwise store default.
      if (contactLocale.value && supportedLocales.value.includes(contactLocale.value)) {
        return contactLocale.value;
      }
      if (supportedLocales.value.includes(savedLocale.value)) {
        return savedLocale.value;
      }
      // No profile preference and no explicit selection — first-time visit. If the current
      // hostname exists in ccTLD settings, honor that language mapping; otherwise keep the
      // store default.
      if (countryCodeTLDLanguage.value && supportedLocales.value.includes(countryCodeTLDLanguage.value)) {
        return countryCodeTLDLanguage.value;
      }
      return defaultLocale.value;
    }
    let locale: string = defaultLocale.value;

    if (supportedLocales.value.includes(localeInPath) || OVERLAY_LOCALES.includes(localeInPath)) {
      locale = localeInPath;
    } else if (contactLocale.value && supportedLocales.value.includes(contactLocale.value)) {
      locale = contactLocale.value;
    } else if (supportedLocales.value.includes(savedLocale.value)) {
      locale = savedLocale.value;
    } else if (countryCodeTLDLanguage.value) {
      locale = countryCodeTLDLanguage.value;
    } else {
      locale = getGeolocationLocale();
    }

    return locale;
  });

  const currentLanguage = computed<ILanguage>(
    () =>
      supportedLanguages.value.find((language) => language.twoLetterLanguageName === currentLocale.value) ||
      defaultLanguage.value,
  );

  async function fetchLocaleMessages(locale: string): Promise<Record<string, unknown>> {
    try {
      const { data } = await useFetch<Record<string, unknown>>(`/api/resources/${locale}`).json<
        Record<string, unknown>
      >();

      if (data.value) {
        return data.value;
      } else {
        return import(`../../../locales/en.json`).then((m) => m.default);
      }
    } catch (error) {
      // Fallback to English or any static import if the fetch fails
      return import("../../../locales/en.json").then((m) => m.default);
    }
  }

  async function setLocale(i18n: I18n, locale: string): Promise<void> {
    const checkedLocale =
      themeContext.value.availableLanguages.find((language) => language.twoLetterLanguageName === locale)
        ?.twoLetterLanguageName ?? defaultLanguage.value.twoLetterLanguageName;
    let messages = i18n.global.getLocaleMessage(checkedLocale);

    if (!Object.keys(messages).length) {
      messages = await fetchLocaleMessages(checkedLocale);
      i18n.global.setLocaleMessage(checkedLocale, messages);
    }

    (i18n.global as unknown as Composer).locale.value = checkedLocale;

    savedLocale.value = checkedLocale;

    setLocaleForYup({
      mixed: {
        required: i18n.global.t("common.messages.required_field"),
      },
      string: {
        max: ({ max }) => i18n.global.t("common.messages.max_length", { max }),
      },
    });
    /**
     * NOTE:
     * If you need to specify the language setting for headers, such as the "fetch" API, set it here.
     * The following is an example for axios.
     *
     * axios.defaults.headers.common['Accept-Language'] = locale
     */

    document.documentElement.setAttribute("lang", checkedLocale);
  }

  function saveLocale(locale: string, needToReload: boolean = true): void {
    const { pathname } = location;
    const splitPathname = pathname.split("/");
    const localeInPath = splitPathname[1];
    const indexOfRemainingPath = 2;
    const locales = [...OVERLAY_LOCALES, ...supportedLocales.value];
    const path = locales?.includes(localeInPath) ? `/${splitPathname.slice(indexOfRemainingPath).join("/")}` : pathname;

    // An overlay locale (German/French) off a cart page is a content overlay (localized content
    // shown without switching the app-wide culture), so the pre-change locale must not be captured.
    // Everywhere else — including an overlay locale selected as a cart language — savedLocaleCopy is
    // updated.
    const isContentOverlay = OVERLAY_LOCALES.includes(locale) && !isCartPage.value;
    if (!isContentOverlay && !languagesAvailableExclusivelyInCart.find((lang) => lang === savedLocale.value)) {
      savedLocaleCopy.value = isCartPage.value ? savedLocale.value : locale;
    }

    savedCartLanguage.value = locale;
    savedLocale.value = locale;

    if (needToReload) {
      let newPath = locale === defaultLocale.value ? path : `/${locale}${path}`;

      // Preserve all query parameters when changing language
      if (location.search) {
        newPath += location.search;
      }

      location.href = newPath;
    }
  }

  function getGeolocationLocale(): string {
    const languageForCountryCode = getGeolocationSupportedLanguage();

    return languageForCountryCode
      ? languageForCountryCode.twoLetterLanguageName
      : defaultLanguage.value.twoLetterLanguageName;
  }

  function getGeolocationSupportedLanguage(isCartLanguage: boolean = false): ILanguage | undefined {
    const countryCodeValue =
      countryCodeToLanguageAndCurrencyMap[
        themeContext.value.countryCode as keyof typeof countryCodeToLanguageAndCurrencyMap
      ];
    if (countryCodeValue) {
      return isCartLanguage
        ? themeContext.value.availableLanguages.find(
            (item) =>
              item.twoLetterLanguageName ===
              (languagesAvailableExclusivelyInCart.find((language) => language === countryCodeValue.Language) ?? ""),
          )
        : supportedLanguages.value.find((language) => language.twoLetterLanguageName === countryCodeValue.Language);
    }
  }

  onMounted(async () => {
    if (!themeContext.value) {
      const store = (await getStore(
        IS_DEVELOPMENT ? extractHostname(import.meta.env.APP_BACKEND_URL as string) : window.location.hostname,
      )) as StoreResponseType;
      const featureFlags = new FeatureFlags(store.settings.modules);
      const optimizelyHost = featureFlags.getValue("Thorlabs.Commerce", "Optimizely.Host") as string;
      const optimizelyCdn = featureFlags.getValue("Thorlabs.Commerce", "Optimizely.CDN") as string;
      const optimizelyUrl = optimizelyCdn != null && optimizelyCdn != "" ? optimizelyCdn : optimizelyHost;
      const countryCode = await getCountryCode(optimizelyUrl);

      await fetchThemeContext(store, countryCode);
    }
  });

  return {
    savedLocale,
    savedLocaleCopy,
    savedCartLanguage,
    defaultLanguage,
    defaultLocale,
    supportedLanguages,
    supportedLocales,
    currentLocale,
    currentLanguage,
    getGeolocationSupportedLanguage,
    setLocale,
    saveLocale,
    fetchLocaleMessages,
    isCartPage,
    userDeniedCartLanguageChange,
    languagesAvailableExclusivelyInCart,
  };
}
export const useLanguages = createSharedComposable(_useLanguages);
